import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, MessageCircle, Users, UserCheck, Briefcase, Mail, Settings, 
  User, LogOut, X, Menu, Newspaper, FolderOpen, CalendarDays, Handshake, 
  BookUser, Heart, Sparkles, Video, GraduationCap, ChevronRight, ChevronLeft,
  Headphones, Shield, Crown, Building2, Bell, Search, Command, Zap, Coffee,
  MessageSquare, Phone, FileText, Target, Award, BarChart3, Layers, ShieldAlert, Inbox,
  Copy, Check, CircleDot, Sun, Moon, Sunrise, Sunset
} from 'lucide-react';

const useGreeting = () => {
  const [g, setG] = useState({ label: 'Welcome back', Icon: Sparkles });
  useEffect(() => {
    const compute = () => {
      const h = new Date().getHours();
      if (h < 5)  setG({ label: 'Working late',   Icon: Moon });
      else if (h < 12) setG({ label: 'Good morning',  Icon: Sunrise });
      else if (h < 17) setG({ label: 'Good afternoon', Icon: Sun });
      else if (h < 21) setG({ label: 'Good evening',  Icon: Sunset });
      else             setG({ label: 'Good evening',  Icon: Moon });
    };
    compute();
    const id = setInterval(compute, 60000);
    return () => clearInterval(id);
  }, []);
  return g;
};

const StaffSidebar = ({ 
  activePage, setActivePage, onLogout, isSidebarOpen, setIsSidebarOpen, 
  currentUser, unreadChatsCount = 0, unreadStaffMessages = 0, pendingLeaveCount = 0,
  isCollapsed = false, setIsCollapsed = () => {}, securityAccess = false, dmcaAccess = false
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredSection, setHoveredSection] = useState(null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const greeting = useGreeting();
  const GreetingIcon = greeting.Icon;

  const handleCopyUsername = (e) => {
    e.stopPropagation();
    if (!currentUser?.username) return;
    navigator.clipboard?.writeText(`@${currentUser.username}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  
  const handlePageChange = (pageId) => {
    setActivePage(pageId);
    navigate(`/staff/${pageId}`);
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      'superadmin': { label: 'Super Admin', color: 'from-amber-500 to-orange-600', icon: Crown, glow: 'shadow-amber-500/30' },
      'executive': { label: 'Executive', color: 'from-violet-500 to-purple-600', icon: Shield, glow: 'shadow-violet-500/30' },
      'admin': { label: 'Admin', color: 'from-cyan-500 to-blue-600', icon: Shield, glow: 'shadow-cyan-500/30' },
      'manager': { label: 'Manager', color: 'from-emerald-500 to-green-600', icon: Users, glow: 'shadow-emerald-500/30' },
      'staff': { label: 'Staff', color: 'from-slate-500 to-slate-600', icon: User, glow: 'shadow-slate-500/30' }
    };
    return badges[role] || badges['staff'];
  };

  const menuSections = [
    {
      id: 'main',
      title: null,
      icon: null,
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'cyan', desc: 'Overview & stats' },
        { id: 'nova-pa', label: 'Nova PA', icon: Crown, color: 'rose', desc: 'Personal assistant', roles: ['superadmin'], premium: true }
      ]
    },
    {
      id: 'operations',
      title: 'Operations',
      icon: Headphones,
      color: 'blue',
      items: [
        { id: 'live-chat', label: 'Live Chat', icon: MessageCircle, badge: unreadChatsCount, color: 'cyan', desc: 'Customer support' },
        { id: 'staff-chat', label: 'Staff Chat', icon: MessageSquare, badge: unreadStaffMessages, color: 'indigo', desc: 'Team messaging' },
        { id: 'staff-resources', label: 'Resources', icon: FolderOpen, color: 'orange', desc: 'Documents & files' },
        { id: 'leave-management', label: 'Leave & Shifts', icon: CalendarDays, color: 'green', desc: 'Time management' },
        { id: 'eportal', label: 'E-Portal', icon: GraduationCap, color: 'purple', desc: 'Training & learning' },
        { id: 'wellbeing', label: 'Wellbeing', icon: Heart, color: 'pink', desc: 'Health & wellness' },
        { id: 'staff-hub', label: 'Staff Hub', icon: Sparkles, color: 'amber', desc: 'Games & activities' },
        { id: 'hr-inbox', label: 'HR Inbox', icon: Inbox, color: 'rose', desc: 'Notices, meetings & warnings', badge: 0 }
      ]
    },
    {
      id: 'admin',
      title: 'Administration',
      icon: Shield,
      color: 'purple',
      items: [
        { id: 'conference', label: 'Conference', icon: Video, color: 'red', desc: 'Video calls', roles: ['executive', 'superadmin'] },
        { id: 'people-hub', label: 'People', icon: Users, color: 'cyan', desc: 'Staff, directory & users', roles: ['admin', 'executive', 'superadmin'], newTag: 'Hub' },
        { id: 'portfolio-groups', label: 'Portfolio Groups', icon: Building2, color: 'emerald', desc: 'Groups, leaders & inspections', newTag: 'New' },
        { id: 'recruitment-hub', label: 'Recruitment', icon: Briefcase, color: 'orange', desc: 'Vacancies, applications & talent pool', roles: ['admin', 'executive', 'superadmin'], newTag: 'Hub' },
        { id: 'content-hub', label: 'Content', icon: Newspaper, color: 'cyan', desc: 'News & newsletter', roles: ['admin', 'executive', 'superadmin'], newTag: 'Hub' },
        { id: 'brand-team-hub', label: 'Brand & Team', icon: Crown, color: 'amber', desc: 'Affiliates, portfolio & team page', roles: ['superadmin'], newTag: 'Hub' },
        { id: 'trust-safety-hub', label: 'Trust & Safety', icon: ShieldAlert, color: 'violet', desc: 'Security & DMCA', customAccess: 'trust-safety', newTag: 'Hub' }
      ]
    },
    {
      id: 'account',
      title: 'Account',
      icon: User,
      color: 'slate',
      items: [
        { id: 'my-profile', label: 'Profile & Settings', icon: Settings, color: 'slate', desc: 'Your account', newTag: 'Updated' }
      ]
    }
  ];

  const canAccess = (item) => {
    // Custom access check for security page
    if (item.customAccess === 'security') {
      if (currentUser?.role === 'superadmin') return true;
      return securityAccess;
    }
    // Custom access check for DMCA page
    if (item.customAccess === 'dmca') {
      if (currentUser?.role === 'superadmin') return true;
      return dmcaAccess;
    }
    // Trust & Safety Hub — visible if user has either security or DMCA access
    if (item.customAccess === 'trust-safety') {
      if (currentUser?.role === 'superadmin') return true;
      return securityAccess || dmcaAccess;
    }
    if (!item.roles) return true;
    if (currentUser?.role === 'superadmin') return true;
    return item.roles.includes(currentUser?.role);
  };

  const canAccessSection = (section) => {
    if (!section.roles) return true;
    if (currentUser?.role === 'superadmin') return true;
    return section.roles.some(r => r === currentUser?.role);
  };

  const roleBadge = getRoleBadge(currentUser?.role);
  const RoleIcon = roleBadge.icon;

  // Filter items based on search
  const filteredSections = menuSections.map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (!canAccess(item)) return false;
      if (!searchQuery) return true;
      return item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
             item.desc?.toLowerCase().includes(searchQuery.toLowerCase());
    })
  })).filter(section => {
    if (!canAccessSection(section)) return false;
    return section.items.length > 0;
  });

  const MenuItem = ({ item, isActive }) => {
    const Icon = item.icon;
    const colorClasses = {
      cyan:    { hex: '#00E5FF', bg: 'bg-cyan-500/15',    text: 'text-cyan-300',    ring: 'ring-cyan-500/40',    from: 'from-cyan-500/25',    to: 'to-cyan-500/0' },
      blue:    { hex: '#3B82F6', bg: 'bg-blue-500/15',    text: 'text-blue-300',    ring: 'ring-blue-500/40',    from: 'from-blue-500/25',    to: 'to-blue-500/0' },
      indigo:  { hex: '#6366F1', bg: 'bg-indigo-500/15',  text: 'text-indigo-300',  ring: 'ring-indigo-500/40',  from: 'from-indigo-500/25',  to: 'to-indigo-500/0' },
      purple:  { hex: '#8A2BE2', bg: 'bg-purple-500/15',  text: 'text-purple-300',  ring: 'ring-purple-500/40',  from: 'from-purple-500/25',  to: 'to-purple-500/0' },
      violet:  { hex: '#8B5CF6', bg: 'bg-violet-500/15',  text: 'text-violet-300',  ring: 'ring-violet-500/40',  from: 'from-violet-500/25',  to: 'to-violet-500/0' },
      pink:    { hex: '#EC4899', bg: 'bg-pink-500/15',    text: 'text-pink-300',    ring: 'ring-pink-500/40',    from: 'from-pink-500/25',    to: 'to-pink-500/0' },
      rose:    { hex: '#F43F5E', bg: 'bg-rose-500/15',    text: 'text-rose-300',    ring: 'ring-rose-500/40',    from: 'from-rose-500/25',    to: 'to-rose-500/0' },
      red:     { hex: '#EF4444', bg: 'bg-red-500/15',     text: 'text-red-300',     ring: 'ring-red-500/40',     from: 'from-red-500/25',     to: 'to-red-500/0' },
      orange:  { hex: '#F97316', bg: 'bg-orange-500/15',  text: 'text-orange-300',  ring: 'ring-orange-500/40',  from: 'from-orange-500/25',  to: 'to-orange-500/0' },
      amber:   { hex: '#F59E0B', bg: 'bg-amber-500/15',   text: 'text-amber-300',   ring: 'ring-amber-500/40',   from: 'from-amber-500/25',   to: 'to-amber-500/0' },
      green:   { hex: '#22C55E', bg: 'bg-green-500/15',   text: 'text-green-300',   ring: 'ring-green-500/40',   from: 'from-green-500/25',   to: 'to-green-500/0' },
      emerald: { hex: '#10B981', bg: 'bg-emerald-500/15', text: 'text-emerald-300', ring: 'ring-emerald-500/40', from: 'from-emerald-500/25', to: 'to-emerald-500/0' },
      teal:    { hex: '#14B8A6', bg: 'bg-teal-500/15',    text: 'text-teal-300',    ring: 'ring-teal-500/40',    from: 'from-teal-500/25',    to: 'to-teal-500/0' },
      slate:   { hex: '#94A3B8', bg: 'bg-slate-500/15',   text: 'text-slate-300',   ring: 'ring-slate-500/40',   from: 'from-slate-500/25',   to: 'to-slate-500/0' },
    };
    const colors = colorClasses[item.color] || colorClasses.cyan;

    return (
      <button
        onClick={() => handlePageChange(item.id)}
        data-testid={`sidebar-${item.id}`}
        title={isCollapsed ? item.label : ''}
        className={`group relative w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'gap-3 px-2.5'} py-2.5 rounded-xl overflow-hidden transition-all duration-300 ease-out ${
          isActive
            ? 'bg-slate-900/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
            : 'text-slate-400 hover:bg-slate-900/40 hover:translate-x-[2px]'
        }`}
      >
        {/* Left glow accent bar */}
        <span
          aria-hidden
          className={`absolute left-0 top-1/2 -translate-y-1/2 h-8 w-[3px] rounded-r-full transition-all duration-300 ${
            isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-50 group-hover:opacity-60 group-hover:scale-y-90'
          }`}
          style={{ backgroundColor: colors.hex, boxShadow: isActive ? `0 0 12px ${colors.hex}` : 'none' }}
        />
        {/* Sliding gradient sheen */}
        <span
          aria-hidden
          className={`absolute inset-0 rounded-xl bg-gradient-to-r ${colors.from} ${colors.to} transition-opacity duration-300 ${
            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'
          }`}
        />

        <div className={`relative shrink-0 ${isCollapsed ? 'w-10 h-10' : 'w-9 h-9'} rounded-xl flex items-center justify-center transition-all duration-300 ${
          isActive
            ? `${colors.bg} ring-1 ${colors.ring} shadow-[0_4px_20px_-4px_${colors.hex}]`
            : 'bg-slate-900/70 ring-1 ring-slate-800/60 group-hover:ring-slate-700 group-hover:bg-slate-800/70'
        }`}
        style={isActive ? { boxShadow: `0 6px 24px -8px ${colors.hex}66` } : {}}
        >
          <Icon
            className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} transition-all duration-300 ${
              isActive
                ? `${colors.text} scale-110`
                : 'text-slate-500 group-hover:text-slate-200 group-hover:scale-105'
            }`}
          />
          {item.premium && !isActive && (
            <span aria-hidden className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ backgroundColor: colors.hex, boxShadow: `0 0 6px ${colors.hex}` }} />
          )}
          {isCollapsed && item.badge > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-gradient-to-br from-red-500 to-rose-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-red-500/40 animate-pulse ring-2 ring-slate-950">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </div>

        {!isCollapsed && (
          <>
            <div className="relative flex-1 text-left min-w-0">
              <span className={`block text-[13px] font-semibold truncate transition-colors ${
                isActive ? 'text-white' : 'text-slate-300 group-hover:text-white'
              }`}>{item.label}</span>
              {item.desc && (
                <span className={`block text-[10px] truncate transition-all duration-300 ${
                  isActive ? `${colors.text} opacity-90` : 'text-slate-600 group-hover:text-slate-500'
                }`}>{item.desc}</span>
              )}
            </div>
            <div className="relative flex items-center gap-1.5 shrink-0">
              {item.newTag && (
                <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-emerald-500/25 to-cyan-500/25 text-emerald-300 ring-1 ring-emerald-500/40 shadow-[0_0_10px_rgba(16,185,129,0.3)]" data-testid={`sidebar-${item.id}-newtag`}>
                  {item.newTag}
                </span>
              )}
              {item.badge > 0 && (
                <span className="min-w-[22px] h-[22px] px-1.5 bg-gradient-to-br from-red-500 to-rose-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-red-500/40 ring-2 ring-slate-950 animate-pulse">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
              {isActive && (
                <ChevronRight className={`w-4 h-4 ${colors.text} transition-transform duration-300 group-hover:translate-x-0.5`} />
              )}
            </div>
          </>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-xl text-slate-300 hover:text-white hover:border-cyan-500/50 transition-all shadow-xl"
        data-testid="mobile-menu-toggle"
      >
        {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full z-40 transition-all duration-300 ease-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isCollapsed ? 'w-[72px]' : 'w-[280px]'} flex flex-col bg-slate-950 border-r border-slate-800/50`}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-cyan-900/20 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-purple-900/10 to-transparent" />
        </div>

        {/* Collapse Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 hover:bg-slate-700 transition-all z-50 shadow-lg"
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>

        {/* Profile Header — Premium Identity Card */}
        <div className={`relative ${isCollapsed ? 'p-3' : 'p-4 pb-5'} border-b border-slate-800/60 overflow-hidden`}>
          {/* Ambient background aura tied to role */}
          {!isCollapsed && (
            <>
              <div
                aria-hidden
                className={`absolute -top-16 -right-10 w-40 h-40 rounded-full blur-3xl opacity-30 pointer-events-none bg-gradient-to-br ${roleBadge.color}`}
              />
              <div
                aria-hidden
                className="absolute inset-x-4 bottom-2 h-px bg-gradient-to-r from-transparent via-slate-700/60 to-transparent pointer-events-none"
              />
            </>
          )}

          <div className={`relative flex items-center ${isCollapsed ? 'justify-center' : 'gap-3.5'}`}>
            {/* Avatar with animated conic gradient halo */}
            <div className="relative shrink-0 group" data-testid="sidebar-profile-avatar">
              {/* Spinning conic ring */}
              <div
                aria-hidden
                className={`absolute inset-0 rounded-2xl opacity-70 blur-[2px] transition-opacity group-hover:opacity-100 ${isCollapsed ? '-m-1' : '-m-1.5'}`}
                style={{
                  background: `conic-gradient(from 0deg, transparent 0deg, ${(roleBadge.color.includes('amber') ? '#F59E0B' : roleBadge.color.includes('violet') ? '#8B5CF6' : roleBadge.color.includes('cyan') ? '#00E5FF' : roleBadge.color.includes('emerald') ? '#10B981' : '#64748B')} 90deg, transparent 180deg, ${(roleBadge.color.includes('amber') ? '#FBBF24' : roleBadge.color.includes('violet') ? '#A78BFA' : roleBadge.color.includes('cyan') ? '#22D3EE' : roleBadge.color.includes('emerald') ? '#34D399' : '#94A3B8')} 270deg, transparent 360deg)`,
                  animation: 'spin 6s linear infinite',
                  maskImage: 'radial-gradient(closest-side, transparent 70%, black 72%, black 100%)',
                  WebkitMaskImage: 'radial-gradient(closest-side, transparent 70%, black 72%, black 100%)',
                }}
              />
              <div className={`relative ${isCollapsed ? 'w-12 h-12' : 'w-[68px] h-[68px]'} rounded-2xl bg-gradient-to-br ${roleBadge.color} p-[2px] shadow-xl ${roleBadge.glow} transition-transform duration-300 group-hover:scale-[1.04]`}>
                <div className="w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center overflow-hidden">
                  {currentUser?.avatar && !avatarFailed ? (
                    <img
                      src={currentUser.avatar.startsWith('http') ? currentUser.avatar : `${process.env.REACT_APP_BACKEND_URL}${currentUser.avatar.startsWith('/api') ? currentUser.avatar : '/api' + currentUser.avatar}`}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <span className={`text-white font-bold ${isCollapsed ? 'text-xl' : 'text-2xl'} tracking-tight`}>
                      {(currentUser?.display_name || currentUser?.username || 'S').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              {/* Role crest — micro-badge on avatar */}
              <div
                className={`absolute -bottom-1 -right-1 ${isCollapsed ? 'w-5 h-5' : 'w-6 h-6'} rounded-full bg-gradient-to-br ${roleBadge.color} flex items-center justify-center ring-[2.5px] ring-slate-950 shadow-lg ${roleBadge.glow}`}
                title={roleBadge.label}
              >
                <RoleIcon className={`${isCollapsed ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-white`} />
              </div>
              {/* Live status pulse (top-left) */}
              <div className="absolute -top-0.5 -left-0.5 flex items-center justify-center">
                <span className="absolute w-3 h-3 rounded-full bg-emerald-400/50 animate-ping" />
                <span className="relative w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-950 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
              </div>
            </div>

            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                {/* Greeting line */}
                <div className="flex items-center gap-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-[0.14em] mb-0.5">
                  <GreetingIcon className="w-3 h-3 text-amber-400/80" />
                  <span>{greeting.label}</span>
                </div>
                {/* Display name */}
                <h2 className="text-white font-bold text-[15px] leading-tight truncate tracking-tight" data-testid="sidebar-profile-name">
                  {currentUser?.display_name || currentUser?.username || 'Staff'}
                </h2>
                {/* Job title with sparkle icon */}
                <p className="mt-0.5 text-slate-400 text-[11px] truncate flex items-center gap-1">
                  <Briefcase className="w-2.5 h-2.5 shrink-0 text-slate-500" />
                  <span className="truncate">{currentUser?.job_title || currentUser?.title || 'Team Member'}</span>
                </p>
                {/* Role Badge + Copy Username row */}
                <div className="mt-2 flex items-center gap-1.5">
                  <div
                    className={`inline-flex items-center gap-1.5 px-2 py-[3px] rounded-md text-[9.5px] font-bold uppercase tracking-[0.09em] bg-gradient-to-r ${roleBadge.color} text-white shadow-md ${roleBadge.glow} ring-1 ring-white/10`}
                    data-testid="sidebar-role-badge"
                  >
                    <RoleIcon className="w-2.5 h-2.5" />
                    {roleBadge.label}
                  </div>
                  <button
                    onClick={handleCopyUsername}
                    title={copied ? 'Copied!' : `Copy @${currentUser?.username || 'username'}`}
                    className="group/copy inline-flex items-center gap-1 px-1.5 py-[3px] rounded-md bg-slate-900/70 ring-1 ring-slate-800 hover:ring-cyan-500/40 hover:bg-slate-900 transition-all text-[9.5px] font-medium text-slate-500 hover:text-cyan-300"
                    data-testid="sidebar-copy-username"
                  >
                    {copied ? <Check className="w-2.5 h-2.5 text-emerald-400" /> : <Copy className="w-2.5 h-2.5" />}
                    <span className="truncate max-w-[64px]">@{currentUser?.username || '—'}</span>
                  </button>
                </div>
                {/* Live activity strip */}
                <div className="mt-2 flex items-center gap-1.5 text-[9.5px] font-medium text-slate-500">
                  <CircleDot className="w-2.5 h-2.5 text-emerald-400 animate-pulse" />
                  <span className="text-emerald-400">Active now</span>
                  <span className="text-slate-700">•</span>
                  <span className="truncate">Zero-Trust session</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Search - Only when expanded */}
        {!isCollapsed && (
          <div className="px-4 py-3 border-b border-slate-800/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-800 rounded-xl pl-10 pr-4 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto ${isCollapsed ? 'px-2 py-3' : 'px-3 py-4'} space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent`}>
          {filteredSections.map((section, sIndex) => {
            const SectionIcon = section.icon;
            return (
              <div key={section.id || sIndex} className="space-y-1">
                {section.title && !isCollapsed && (
                  <div 
                    className="flex items-center gap-2 px-3 py-2 mb-1"
                    onMouseEnter={() => setHoveredSection(section.id)}
                    onMouseLeave={() => setHoveredSection(null)}
                  >
                    {SectionIcon && (
                      <SectionIcon className={`w-3.5 h-3.5 transition-colors ${
                        hoveredSection === section.id ? 'text-cyan-400' : 'text-slate-600'
                      }`} />
                    )}
                    <h3 className={`text-[11px] font-bold uppercase tracking-wider transition-colors ${
                      hoveredSection === section.id ? 'text-cyan-400' : 'text-slate-600'
                    }`}>
                      {section.title}
                    </h3>
                    <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent" />
                  </div>
                )}
                {isCollapsed && section.title && (
                  <div className="flex justify-center py-1">
                    <div className="w-8 h-px bg-slate-800" />
                  </div>
                )}
                <div className="space-y-0.5">
                  {section.items.map(item => (
                    <MenuItem key={item.id} item={item} isActive={activePage === item.id} />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Quick Actions - Only when expanded */}
        {!isCollapsed && (
          <div className="px-4 py-3 border-t border-slate-800/50">
            <div className="flex items-center gap-2">
              <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-slate-900/50 border border-slate-800 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/30 transition-all text-xs">
                <Bell className="w-3.5 h-3.5" />
                <span>Alerts</span>
              </button>
              <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-slate-900/50 border border-slate-800 text-slate-400 hover:text-emerald-400 hover:border-emerald-500/30 transition-all text-xs">
                <Coffee className="w-3.5 h-3.5" />
                <span>Break</span>
              </button>
            </div>
          </div>
        )}

        {/* Logout */}
        <div className={`relative ${isCollapsed ? 'p-2' : 'p-4'} border-t border-slate-800/50`}>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            title={isCollapsed ? 'Sign Out' : ''}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-center gap-2'} px-4 py-3 rounded-xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 text-red-400 hover:from-red-500/20 hover:to-orange-500/20 hover:border-red-500/40 hover:text-red-300 transition-all shadow-lg shadow-red-500/5`}
          >
            <LogOut className="w-4 h-4" />
            {!isCollapsed && <span className="font-semibold text-sm">Sign Out</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default StaffSidebar;
